<div class="receipt-detail" [class.busy]="Busy || Loading">

  @if (!Ready) {
  <div class="muted">Loading…</div>
  } @else {

  <!-- Header: receipt #, stage, exception, next action -->
  <div class="head">
    <div class="head-title">
      @if (IsNew) {
      <span class="rcpt-no">New receipt</span>
      } @else {
      <span class="rcpt-no">Receipt #{{ ChargeLogID }}</span>
      <span [class]="stageClass(Stage)">{{ Stage }}</span>
      @if (Receipt?.EffectiveExceptionCode) {
      <span class="badge badge-exception">{{ Receipt.EffectiveExceptionCode }}</span>
      }
      @if (IsReturn) {
      <span class="badge badge-return">Return of #{{ Receipt.ReversalOfChargeLogID }}</span>
      }
      @if (WasReturned) {
      <span class="badge badge-return">Returned by #{{ Receipt.ReversedByChargeLogID }}</span>
      }
      }
    </div>
    @if (!IsNew && Receipt) {
    <div class="head-info">
      <span>{{ Receipt.CompanyName ?? 'No customer' }}</span>
      @if (Receipt.NextAction) { <span class="next">Next: {{ Receipt.NextAction }}</span> }
      @if (Receipt.OwnerName) { <span>Owner: {{ Receipt.OwnerName }}</span> }
    </div>
    }
  </div>

  @if (Error) {
  <div class="save-error">{{ Error }}</div>
  }

  <rms-tab-group>

    <!-- =============================== RECEIPT =============================== -->
    <rms-tab label="Receipt">
      <ng-template>
        <div class="tab-pane">

          <div class="grid">
            <div class="field span-2">
              <label>Customer</label>
              @if (CanChooseCustomer) {
              <rms-search-select name="CompanyID" [options]="Customers" labelKey="Label" valueKey="CompanyID"
                                 [required]="true" [(ngModel)]="Form.CompanyID"></rms-search-select>
              } @else {
              <div class="readonly">
                <span>{{ Receipt?.CompanyName ?? '—' }}{{ Receipt?.CompanyID ? ' · C-' + Receipt.CompanyID : '' }}</span>
                @if (CanChangeCustomer) {
                <button class="change-link" type="button" [disabled]="Busy" (click)="startChangeCustomer()">Change</button>
                }
              </div>
              }
            </div>
            <div class="field">
              <label>Order # (optional)</label>
              <rms-input name="OrderID" type="text" [disabled]="!CanEdit" [(ngModel)]="Form.OrderID"></rms-input>
            </div>

            <div class="field">
              <label>Payment Type</label>
              <rms-select name="PaymentDetailTypeID" [options]="DetailTypes" labelKey="DetailTypeName" valueKey="PaymentDetailTypeID"
                          placeholder="Select payment type…" [required]="true" [disabled]="!CanEdit"
                          [(ngModel)]="Form.PaymentDetailTypeID"></rms-select>
            </div>
            <div class="field">
              <label>Bank</label>
              <rms-select name="BankID" [options]="Banks" labelKey="BankName" valueKey="BankID"
                          [required]="true" [disabled]="!CanEdit" [(ngModel)]="Form.BankID"></rms-select>
            </div>
            <div class="field">
              <label>Check #</label>
              <rms-input name="CheckNumber" type="text" [disabled]="!CanEdit" [(ngModel)]="Form.CheckNumber"></rms-input>
            </div>

            <div class="field">
              <label>Amount (gross)</label>
              <rms-input name="Amount" type="currency" [required]="true" [disabled]="!CanEdit" [(ngModel)]="Form.Amount"></rms-input>
            </div>
            <div class="field">
              <label>Customer Fee</label>
              <rms-input name="CustomerFeeAmount" type="currency" [disabled]="!CanEdit" [(ngModel)]="Form.CustomerFeeAmount"></rms-input>
              <span class="field-hint">Surcharge the customer paid (not applied to orders)</span>
            </div>
            <div class="field">
              <label>Processor Fee</label>
              <rms-input name="ProcessorFeeAmount" type="currency" [disabled]="!CanEdit" [(ngModel)]="Form.ProcessorFeeAmount"></rms-input>
              <span class="field-hint">Net deposit: {{ NetDeposit | currency: 'USD' : 'symbol' : '1.2-2' }}</span>
            </div>

            <div class="field">
              <label>Received Date</label>
              <rms-date name="ReceivedDate" label="" [disabled]="!CanEdit" [(ngModel)]="Form.ReceivedDate"></rms-date>
            </div>
            <div class="field">
              <label>Deposit Date</label>
              <rms-date name="DepositDate" label="" [disabled]="!CanEdit" [(ngModel)]="Form.DepositDate"></rms-date>
              <span class="field-hint">Date the money reached the bank (needed to post)</span>
            </div>
            <div class="field">
              <label>Masked Reference</label>
              <rms-input name="MaskedReference" type="text" placeholder="e.g. ****4242" [disabled]="!CanEdit"
                         [(ngModel)]="Form.MaskedReference"></rms-input>
              <span class="field-hint">Last 4 only — never the full card or account number</span>
            </div>

            <div class="field">
              <label>Processor</label>
              <rms-input name="ProcessorName" type="text" placeholder="Braintree, PayPal…" [disabled]="!CanEdit"
                         [(ngModel)]="Form.ProcessorName"></rms-input>
            </div>
            <div class="field span-2">
              <label>Processor Reference</label>
              <rms-input name="ProcessorReference" type="text" [disabled]="!CanEdit" [(ngModel)]="Form.ProcessorReference"></rms-input>
            </div>

            <div class="field span-3">
              <label>Notes</label>
              <rms-input name="Notes" type="text" [disabled]="!CanEdit" [(ngModel)]="Form.Notes"></rms-input>
            </div>
          </div>

          @if (!IsNew && Receipt) {
          <div class="amounts">
            <div><span>Applied to orders</span> {{ Receipt.AppliedAmount | currency: 'USD' : 'symbol' : '1.2-2' }}</div>
            <div [class.warn]="Receipt.UnappliedAmount > 0.005"><span>Unapplied</span> {{ Receipt.UnappliedAmount | currency: 'USD' : 'symbol' : '1.2-2' }}</div>
            @if (Receipt.PostingDate) {
            <div><span>Posted</span> {{ Receipt.PostingDate | date: 'MM/dd/yyyy' : 'UTC' }} {{ Receipt.PostedByName ? '· ' + Receipt.PostedByName : '' }}</div>
            }
            @if (Receipt.LedgerTransactionID) {
            <div><span>Ledger #</span> {{ Receipt.LedgerTransactionID }}</div>
            }
          </div>
          }

          <!-- Braintree status lookup (7.2.28) -->
          @if (!IsNew && BraintreeId) {
          <div class="bt-box">
            <div class="bt-head">
              <span>Braintree <span class="mono">{{ BraintreeId }}</span></span>
              <button class="btn-bg-blue" type="button" [disabled]="BtLoading" (click)="checkBraintree()">
                {{ BtLoading ? 'Checking…' : 'Check Braintree' }}
              </button>
            </div>
            @if (BtError) {
            <div class="save-error">{{ BtError }}</div>
            }
            @if (BtInfo?.Transaction; as t) {
            <div class="bt-grid">
              <div><span>Status</span> <b>{{ t.Status }}</b>{{ BtInfo?.Environment === 'sandbox' ? ' (sandbox)' : '' }}</div>
              <div><span>Amount</span> {{ t.Amount | currency: 'USD' : 'symbol' : '1.2-2' }} · {{ t.Type }}</div>
              <div><span>Order</span> {{ t.OrderId ?? '—' }}</div>
              <div><span>Created</span> {{ t.CreatedAt | date: 'MM/dd/yyyy h:mm a' }}</div>
              <div><span>Settled</span> {{ t.SettlementDate ?? '—' }}</div>
              <div><span>Disbursed</span> {{ t.DisbursementDate ?? '—' }}</div>
              @if (t.Last4) { <div><span>Card</span> ****{{ t.Last4 }}</div> }
              @if (t.ProcessorResponse) { <div><span>Processor</span> {{ t.ProcessorResponse }}</div> }
            </div>
            @if (t.StatusHistory?.length) {
            <div class="bt-history">
              @for (h of t.StatusHistory; track $index) {
              <span>{{ h.Timestamp | date: 'MM/dd h:mm a' }} · {{ h.Status }}</span>
              }
            </div>
            }
            }
          </div>
          }

          <div class="actions">
            @if (CanEdit) {
            <button class="btn-bg-green" type="button" [disabled]="Busy" (click)="save()">{{ IsNew ? 'Create Receipt' : 'Save' }}</button>
            }
            @if (CanMarkProcessing) {
            <button class="btn-bg-yellow" type="button" [disabled]="Busy" (click)="markProcessing()">Mark Processing</button>
            }
            @if (CanPost) {
            <button class="btn-bg-blue" type="button" [disabled]="Busy" (click)="post()"
                    title="Saves your changes, then posts to the ledger with the deposit date and bank above">
              Post to Ledger
            </button>
            }
          </div>

          @if (!IsNew && Receipt?.ExceptionCode && !IsPosted) {
          <div class="note">This receipt has an exception ({{ Receipt.ExceptionCode }}). Clear it on the Follow-up tab before posting.</div>
          }

          <!-- Return: posted receipts only -->
          @if (CanReturn) {
          <fieldset class="return-box">
            <legend>Returned / bounced payment</legend>
            <div class="grid">
              <div class="field span-2">
                <label>Reason</label>
                <rms-input name="ReturnReason" type="text" placeholder="NSF check, chargeback, ACH return R01…" [(ngModel)]="ReturnReason"></rms-input>
              </div>
              <div class="field">
                <label>Return Date</label>
                <rms-date name="ReturnDate" label="" [(ngModel)]="ReturnDate"></rms-date>
              </div>
            </div>
            <div class="actions">
              <button class="btn-bg-red" type="button" [disabled]="Busy || !ReturnReason.trim()" (click)="returnPayment()">Return Payment</button>
            </div>
          </fieldset>
          }
        </div>
      </ng-template>
    </rms-tab>

    <!-- =============================== ALLOCATE =============================== -->
    <rms-tab label="Allocate">
      <ng-template>
        <div class="tab-pane">
          @if (IsNew) {
          <div class="muted">Create the receipt first.</div>
          } @else if (!CanAllocate) {
          <div class="muted">
            @if (IsReturn) { A return receipt is not allocated to orders. }
            @else if (WasReturned) { This receipt was returned — its cash cannot be allocated. }
            @else if (!Receipt?.CompanyID) { Set the customer on the Receipt tab first. }
            @else { This receipt has no cash to allocate. }
          </div>
          } @else {

          <div class="amounts top">
            <div><span>Available</span> {{ Available | currency: 'USD' : 'symbol' : '1.2-2' }}</div>
            <div><span>Allocated</span> {{ Allocated | currency: 'USD' : 'symbol' : '1.2-2' }}</div>
            <div [class.warn]="Unapplied > 0.005" [class.bad]="Unapplied < -0.005">
              <span>{{ Unapplied < -0.005 ? 'Over by' : 'Unapplied' }}</span>
              {{ (Unapplied < 0 ? -Unapplied : Unapplied) | currency: 'USD' : 'symbol' : '1.2-2' }}
            </div>
          </div>

          <table class="grid-table">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Sale Date</th>
                <th>Status</th>
                <th class="num">Order Total</th>
                <th class="num">Open</th>
                <th class="num">Apply</th>
                <th class="num">Open After</th>
              </tr>
            </thead>
            <tbody>
              @for (l of Lines; track l.OrderID) {
              <tr [class.has-apply]="(l.Apply ?? 0) > 0">
                <td>{{ l.OrderID }}</td>
                <td>{{ l.SaleDate | date: 'MM/dd/yyyy' : 'UTC' }}</td>
                <td>{{ l.OrderStatusName ?? '—' }}</td>
                <td class="num">{{ l.OrderTotal | currency: 'USD' : 'symbol' : '1.2-2' }}</td>
                <td class="num">{{ l.OpenBeforeThisReceipt | currency: 'USD' : 'symbol' : '1.2-2' }}</td>
                <td class="num">
                  <input class="amt-input" type="number" min="0" step="0.01" [class.bad]="lineError(l)"
                         [title]="lineError(l)" [(ngModel)]="l.Apply" />
                </td>
                <td class="num">{{ (l.OpenBeforeThisReceipt - (l.Apply ?? 0)) | currency: 'USD' : 'symbol' : '1.2-2' }}</td>
              </tr>
              } @empty {
              <tr><td colspan="7" class="muted">This customer has no open orders.</td></tr>
              }
            </tbody>
          </table>

          <div class="actions">
            <button class="btn-bg-slate" type="button" [disabled]="Busy" (click)="clearAllocations()">Clear</button>
            <button class="btn-bg-yellow" type="button" [disabled]="Busy || !Lines.length" (click)="fillOldestFirst()">Fill Oldest First</button>
            <button class="btn-bg-green" type="button" [disabled]="Busy || !AllocationValid" (click)="saveAllocations()">Save Allocations</button>
          </div>
          <div class="field-hint">Whatever is not applied stays on the receipt as unapplied cash. Setting an existing line to 0 removes it (kept in history).</div>
          }

          @if (Allocations.length) {
          <h4>Allocation history</h4>
          <table class="grid-table small">
            <thead>
              <tr><th>Order #</th><th class="num">Amount</th><th>Status</th><th>Method</th><th>Created</th><th>Canceled</th></tr>
            </thead>
            <tbody>
              @for (a of Allocations; track a.OrderPaymentID) {
              <tr [class.canceled]="a.StatusID === 7 || a.StatusID === 8">
                <td>{{ a.OrderID }}</td>
                <td class="num">{{ a.Amount | currency: 'USD' : 'symbol' : '1.2-2' }}</td>
                <td>{{ a.StatusName }}</td>
                <td>{{ a.MethodName ?? '—' }}</td>
                <td>{{ a.CreatedDate | date: 'MM/dd/yyyy' }}</td>
                <td>{{ a.CanceledDate ? (a.CanceledDate | date: 'MM/dd/yyyy') : '' }}</td>
              </tr>
              }
            </tbody>
          </table>
          }
        </div>
      </ng-template>
    </rms-tab>

    <!-- =============================== FOLLOW-UP =============================== -->
    <rms-tab label="Follow-up">
      <ng-template>
        <div class="tab-pane">
          @if (IsNew) {
          <div class="muted">Create the receipt first.</div>
          } @else {
          @if (Receipt?.IsStale && !Receipt?.ExceptionCode) {
          <div class="note">Flagged automatically as <b>STALE</b> (unposted and received more than 6 months ago, or a type that never posts). Post it, return it, or set an exception and owner below.</div>
          }
          <div class="grid">
            <div class="field">
              <label>Exception</label>
              <rms-select name="ExceptionCode" [options]="ExceptionOptions" labelKey="label" valueKey="value"
                          [(ngModel)]="ExceptionCode"></rms-select>
            </div>
            <div class="field">
              <label>Owner</label>
              <rms-select name="OwnerChoice" [options]="OwnerOptions" labelKey="label" valueKey="value"
                          [(ngModel)]="OwnerChoice"></rms-select>
            </div>
            <div class="field">
              <label>Next Action</label>
              <rms-input name="NextAction" type="text" placeholder="Leave empty for the automatic one" [(ngModel)]="NextAction"></rms-input>
            </div>
            <div class="field span-3">
              <label>Exception Note</label>
              <rms-input name="ExceptionNote" type="text" [(ngModel)]="ExceptionNote"></rms-input>
            </div>
          </div>
          <div class="actions">
            <button class="btn-bg-green" type="button" [disabled]="Busy" (click)="saveFollowup()">Save Follow-up</button>
          </div>

          <div class="note-row">
            <div class="field grow">
              <label>Add a note to the history</label>
              <rms-input name="NoteText" type="text" [(ngModel)]="NoteText"></rms-input>
            </div>
            <button class="btn-bg-blue" type="button" [disabled]="Busy || !NoteText.trim()" (click)="addNote()">Add Note</button>
          </div>
          }
        </div>
      </ng-template>
    </rms-tab>

    <!-- =============================== HISTORY =============================== -->
    <rms-tab label="History">
      <ng-template>
        <div class="tab-pane">
          @if (IsNew) {
          <div class="muted">Create the receipt first.</div>
          } @else {

          @if (Related.length) {
          <h4>Related receipts</h4>
          <table class="grid-table small">
            <thead><tr><th>Receipt #</th><th>Relation</th><th>Received</th><th>Posted</th><th class="num">Amount</th><th>Stage</th></tr></thead>
            <tbody>
              @for (r of Related; track r.ChargeLogID) {
              <tr>
                <td>{{ r.ChargeLogID }}</td>
                <td>{{ r.Relation }}</td>
                <td>{{ r.ReceivedDate | date: 'MM/dd/yyyy' : 'UTC' }}</td>
                <td>{{ r.PostingDate | date: 'MM/dd/yyyy' : 'UTC' }}</td>
                <td class="num">{{ r.GrossAmount | currency: 'USD' : 'symbol' : '1.2-2' }}</td>
                <td><span [class]="stageClass(r.Stage)">{{ r.Stage }}</span></td>
              </tr>
              }
            </tbody>
          </table>
          }

          <h4>Ledger entries</h4>
          <table class="grid-table small">
            <thead><tr><th>Date</th><th>Account</th><th>Description</th><th class="num">Deposit</th><th class="num">Withdrawal</th></tr></thead>
            <tbody>
              @for (l of Ledger; track l.TransactionID) {
              <tr>
                <td>{{ l.TransactionDate | date: 'MM/dd/yyyy' : 'UTC' }}</td>
                <td>{{ l.AccountNumber }} {{ l.AccountName }}</td>
                <td>{{ l.TransactionDescription }}</td>
                <td class="num">{{ l.DepositAmount ? (l.DepositAmount | currency: 'USD' : 'symbol' : '1.2-2') : '' }}</td>
                <td class="num">{{ l.WithdrawalAmount ? (l.WithdrawalAmount | currency: 'USD' : 'symbol' : '1.2-2') : '' }}</td>
              </tr>
              } @empty {
              <tr><td colspan="5" class="muted">Not posted to the ledger yet.</td></tr>
              }
            </tbody>
          </table>

          <h4>Actions</h4>
          <table class="grid-table small">
            <thead><tr><th>When</th><th>Who</th><th>Action</th><th>Detail</th></tr></thead>
            <tbody>
              @for (h of History; track h.PaymentActionID) {
              <tr>
                <td class="nowrap">{{ h.ActionDate | date: 'MM/dd/yyyy h:mm a' }}</td>
                <td>{{ h.ActionByName }}</td>
                <td>{{ h.ActionType }}</td>
                <td>{{ h.ActionDetail }}</td>
              </tr>
              } @empty {
              <tr><td colspan="4" class="muted">No actions recorded yet (migrated receipts start empty).</td></tr>
              }
            </tbody>
          </table>
          }
        </div>
      </ng-template>
    </rms-tab>

  </rms-tab-group>

  <div class="actions footer">
    <button class="btn-bg-slate" type="button" (click)="close()">Close</button>
  </div>
  }
</div>
